import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNextQuestion, submitAnswer, navigateQuestion, completeTest } from '../services/api';

interface Question {
  id: number;
  question_number: number;
  question_type: string;
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  section_instructions: string | null;
}

export const useTest = () => {
  const [question, setQuestion] = useState<Question | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(40);
  const [canGoBack, setCanGoBack] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());

  const navigate = useNavigate();
  const sessionId = localStorage.getItem('sessionId');

  const loadNextQuestion = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const response = await getNextQuestion(Number(sessionId));
      setQuestion(response.data.question);
      setCurrentIndex(response.data.current_index);
      setTotalQuestions(response.data.total_questions);
      setCanGoBack(response.data.can_go_back);
      setTimeRemaining(response.data.time_remaining_seconds);
      setQuestionStartTime(Date.now());
      setSelectedAnswer('');
    } catch (err: any) {
      if (err.response?.status === 404) {
        const attemptId = localStorage.getItem('attemptId');
        if (attemptId) navigate(`/results/${attemptId}`);
      }
    } finally {
      setLoading(false);
    }
  }, [sessionId, navigate]);

  useEffect(() => {
    if (!sessionId) { navigate('/dashboard'); return; }
    loadNextQuestion();
  }, [sessionId, navigate, loadNextQuestion]);

  const handleSubmit = useCallback(async () => {
    if (!question || !sessionId) return;
    const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    setLoading(true);
    try {
      const response = await submitAnswer({
        session_id: Number(sessionId),
        question_id: question.id,
        selected_answer: selectedAnswer || '',
        time_spent_seconds: timeSpent,
      });
      
      // Check if test is complete
      if (response.data.test_complete) {
        const attemptId = localStorage.getItem('attemptId');
        if (attemptId) {
          navigate(`/results/${attemptId}`);
        }
        return;
      }
      
      await loadNextQuestion();
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  }, [question, sessionId, selectedAnswer, questionStartTime, loadNextQuestion, navigate]);

  const handleNavigate = useCallback(async (direction: 'back' | 'forward') => {
    if (!sessionId) return;
    setLoading(true);
    try {
      await navigateQuestion({ session_id: Number(sessionId), direction });
      await loadNextQuestion();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [sessionId, loadNextQuestion]);

  const handleQuit = useCallback(async () => {
    if (window.confirm('Êtes-vous sûr de vouloir quitter ? Votre progression sera sauvegardée.')) {
      const attemptId = localStorage.getItem('attemptId');
      if (attemptId) {
        try {
          await completeTest(Number(attemptId));
          navigate(`/results/${attemptId}`);
        } catch (err) {
          console.error('Error completing test:', err);
          // Still navigate to results even if error (test might already be completed)
          navigate(`/results/${attemptId}`);
        }
      } else {
        // No attemptId, just go back to dashboard
        localStorage.removeItem('sessionId');
        navigate('/dashboard');
      }
    }
  }, [navigate]);

  return {
    question,
    selectedAnswer,
    loading,
    currentIndex,
    totalQuestions,
    canGoBack,
    timeRemaining,
    setSelectedAnswer,
    handleSubmit,
    handleNavigate,
    handleQuit,
    loadNextQuestion,
  };
};
